import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignsApi } from '../api';
import { toast } from 'sonner';
import {
  Plus, Play, Pause, Copy, Trash2, Eye, StopCircle, Loader2, Send, MoreHorizontal
} from 'lucide-react';

const STATUS_MAP = {
  draft: { bg: 'bg-[#E8EAED]', text: 'text-[#5F6368]' },
  active: { bg: 'bg-[#E6F4EA]', text: 'text-[#1E8E3E]' },
  paused: { bg: 'bg-[#FEF7E0]', text: 'text-[#F9AB00]' },
  completed: { bg: 'bg-[#E8F0FE]', text: 'text-[#1A73E8]' },
  stopped: { bg: 'bg-[#FCE8E6]', text: 'text-[#D93025]' },
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCampaigns = async () => {
    setLoading(true);
    try { setCampaigns(await campaignsApi.getAll()); }
    catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handle = async (action, id) => {
    try {
      if (action === 'pause') { await campaignsApi.pause(id); toast.success('Campaign paused'); }
      if (action === 'resume') { await campaignsApi.resume(id); toast.success('Campaign resumed'); }
      if (action === 'stop') { await campaignsApi.stop(id); toast.success('Campaign stopped'); }
      if (action === 'duplicate') { await campaignsApi.duplicate(id); toast.success('Campaign duplicated'); }
      if (action === 'delete') {
        if (!window.confirm('Delete this campaign?')) return;
        await campaignsApi.delete(id); toast.success('Campaign deleted');
      }
      fetchCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div data-testid="outreach-campaigns">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-[#202124]">Campaigns</h1>
          <p className="text-sm text-[#5F6368]">{campaigns.length} campaigns</p>
        </div>
        <button onClick={() => navigate('/outreach/campaigns/new')}
          className="flex items-center gap-1.5 px-5 py-2.5 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-sm font-medium transition-colors shadow-sm" data-testid="create-campaign-btn">
          <Plus className="w-4 h-4" /> Create Campaign
        </button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#F1F3F4] text-left">
              {['Name','Status','Total','Sent','Opened','Clicked','Replied','Bounced','Created','Actions'].map(h => (
                <th key={h} className="px-5 py-3 text-xs font-semibold text-[#5F6368] uppercase tracking-wide">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-5 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#1A73E8] mx-auto" /></td></tr>
              ) : campaigns.length === 0 ? (
                <tr><td colSpan={10} className="px-5 py-16 text-center">
                  <Send className="w-10 h-10 mx-auto mb-3 text-[#9AA0A6]" />
                  <p className="text-[#5F6368] font-medium">No campaigns yet</p>
                  <p className="text-sm text-[#9AA0A6] mt-1">Create your first campaign to get started</p>
                </td></tr>
              ) : campaigns.map((c) => {
                const st = STATUS_MAP[c.status] || STATUS_MAP.draft;
                return (
                  <tr key={c.id} className="border-b border-[#F1F3F4] hover:bg-[#F8F9FA] transition-colors group">
                    <td className="px-5 py-3 text-[#202124] font-medium cursor-pointer hover:text-[#1A73E8]" onClick={() => navigate(`/outreach/campaigns/${c.id}`)}>{c.name}</td>
                    <td className="px-5 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{c.status}</span></td>
                    <td className="px-5 py-3 text-[#5F6368]">{c.total_leads}</td>
                    <td className="px-5 py-3 text-[#5F6368]">{c.sent}</td>
                    <td className="px-5 py-3 text-[#5F6368]">{c.opened}</td>
                    <td className="px-5 py-3 text-[#5F6368]">{c.clicked}</td>
                    <td className="px-5 py-3 text-[#5F6368]">{c.replied}</td>
                    <td className="px-5 py-3 text-[#5F6368]">{c.bounced}</td>
                    <td className="px-5 py-3 text-[#9AA0A6] text-xs">{fmt(c.created_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => navigate(`/outreach/campaigns/${c.id}`)} title="View" className="p-1.5 text-[#5F6368] hover:text-[#1A73E8] hover:bg-[#E8F0FE] rounded-full"><Eye className="w-4 h-4" /></button>
                        {c.status === 'draft' && <button onClick={() => handle('resume', c.id)} title="Launch" className="p-1.5 text-[#5F6368] hover:text-[#1E8E3E] hover:bg-[#E6F4EA] rounded-full"><Play className="w-4 h-4" /></button>}
                        {c.status === 'active' && <button onClick={() => handle('pause', c.id)} title="Pause" className="p-1.5 text-[#5F6368] hover:text-[#F9AB00] hover:bg-[#FEF7E0] rounded-full"><Pause className="w-4 h-4" /></button>}
                        {c.status === 'paused' && <button onClick={() => handle('resume', c.id)} title="Resume" className="p-1.5 text-[#5F6368] hover:text-[#1E8E3E] hover:bg-[#E6F4EA] rounded-full"><Play className="w-4 h-4" /></button>}
                        {['active','paused'].includes(c.status) && <button onClick={() => handle('stop', c.id)} title="Stop" className="p-1.5 text-[#5F6368] hover:text-[#D93025] hover:bg-[#FCE8E6] rounded-full"><StopCircle className="w-4 h-4" /></button>}
                        <button onClick={() => handle('duplicate', c.id)} title="Duplicate" className="p-1.5 text-[#5F6368] hover:text-[#0891B2] hover:bg-[#E0F7FA] rounded-full"><Copy className="w-4 h-4" /></button>
                        <button onClick={() => handle('delete', c.id)} title="Delete" className="p-1.5 text-[#5F6368] hover:text-[#D93025] hover:bg-[#FCE8E6] rounded-full"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
